import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmt2 } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Wallet, IndianRupee, Loader2 } from "lucide-react";

function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// ---------- PaymentMethodCard ----------
function PaymentMethodCard({ icon, title, subtitle, amount, submitting, submittingKey, disabled, onClick, testId, dimmed }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled || !!submitting}
      className={`text-left rounded-2xl border p-5 transition-shadow ${
        dimmed
          ? "bg-muted/40 border-dashed cursor-not-allowed"
          : "bg-card border-border hover:shadow-[0_18px_45px_-22px_rgba(31,42,68,0.35)]"
      } disabled:opacity-60`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0">{icon}</span>
        <div className="flex-1">
          <div className="font-semibold">{title}</div>
          <div className="text-sm text-muted-foreground">{subtitle}</div>
        </div>
        {submitting === submittingKey
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : amount !== null && <span className="text-sm font-semibold">{amount}</span>
        }
      </div>
    </button>
  );
}

// ---------- Main page ----------
export default function PaymentPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [methods, setMethods] = useState({ stripe: false, razorpay: false });
  const [bookings, setBookings] = useState([]);
  const [submitting, setSubmitting] = useState(null);

  const totalFinal = bookings.reduce((s, b) => s + (b.line_final || 0), 0);

  useEffect(() => {
    (async () => {
      try {
        const [m, b] = await Promise.all([api.get("/payments/methods"), api.get("/bookings")]);
        setMethods(m.data);
        setBookings((b.data || []).filter((x) => x.booking_group_id === groupId));
      } catch (e) { /* ignore */ }
    })();
  }, [groupId]);

  const payStripe = async () => {
    setSubmitting("stripe");
    try {
      const r = await api.post("/payments/stripe/checkout-session", { booking_group_id: groupId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe checkout");
      setSubmitting(null);
    }
  };

  const payRazorpay = async () => {
    setSubmitting("razorpay");
    try {
      const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
      if (!ok) throw new Error("Failed to load Razorpay");
      const order = await api.post("/payments/razorpay/order", { booking_group_id: groupId });
      const options = {
        key: order.data.key_id,
        amount: order.data.amount,
        currency: order.data.currency,
        order_id: order.data.order_id,
        name: "IndoVoyage",
        description: `Booking ${groupId}`,
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              booking_group_id: groupId,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful!");
            navigate(`/booking-confirmation/${groupId}`);
          } catch (e) { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setSubmitting(null) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Razorpay error");
      setSubmitting(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 py-10" data-testid="payment-page">
      <h1 className="font-display text-3xl font-semibold tracking-tight">Choose how to pay</h1>
      <p className="text-muted-foreground mt-1">Booking reference <span className="font-mono">{groupId}</span></p>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 grid gap-4">
          <PaymentMethodCard
            icon={<CreditCard className="h-5 w-5 text-[hsl(var(--peacock))]" />}
            title="Pay with card (Stripe) · USD"
            subtitle="International cards • fast • secure"
            amount={fmt2(totalFinal)}
            submitting={submitting}
            submittingKey="stripe"
            disabled={!methods.stripe}
            onClick={payStripe}
            testId="pay-stripe-button"
          />

          <PaymentMethodCard
            icon={<IndianRupee className="h-5 w-5 text-[hsl(var(--vermilion))]" />}
            title={`Pay in INR (Razorpay)${methods.razorpay ? "" : " — Coming soon"}`}
            subtitle={methods.razorpay ? "Cards, UPI, netbanking & wallets" : "Add RAZORPAY keys to enable"}
            amount={methods.razorpay ? "₹-" : null}
            submitting={submitting}
            submittingKey="razorpay"
            disabled={!methods.razorpay}
            onClick={methods.razorpay ? payRazorpay : undefined}
            testId="pay-razorpay-button"
            dimmed={!methods.razorpay}
          />

          <PaymentMethodCard
            icon={<Wallet className="h-5 w-5" />}
            title="Confirm without paying (demo)"
            subtitle="Skip payment and go to booking confirmation — useful for testing flows."
            amount={null}
            submitting={submitting}
            submittingKey="skip"
            disabled={false}
            onClick={() => navigate(`/booking-confirmation/${groupId}`)}
            testId="pay-skip-button"
          />
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl bg-card border border-border p-5 lg:sticky lg:top-32" data-testid="payment-summary">
            <h2 className="font-semibold mb-3">Order summary</h2>
            <div className="grid gap-2">
              {bookings.map((b) => (
                <div key={b.booking_id} className="flex justify-between text-sm">
                  <span className="truncate pr-2">{b.title}</span>
                  <span>{fmt2(b.line_final)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-xl bg-muted border border-border p-3 flex items-center justify-between" data-testid="payment-summary-total">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-semibold" data-testid="payment-price-breakdown-total">{fmt2(totalFinal)}</span>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">All-inclusive total. Pay securely with your preferred method above.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
